import { Link } from "react-router-dom";
import { HeartHandshake, Building2, Home, ArrowRight, CheckCircle2 } from "lucide-react";
import useSEO from "@/components/site/useSEO";
import PageHero from "@/components/site/PageHero";
import { SectionHead, CTABand } from "@/components/site/ui";
import { Reveal, Stagger, StaggerItem } from "@/components/site/Reveal";
import { ManagerDashboard } from "@/components/site/mockups";

const SECTORS = [
  {
    label: "Supported Living",
    to: "/supported-living",
    icon: HeartHandshake,
    img: "https://images.unsplash.com/photo-1730295003911-460cbe6a3886?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    desc: "Support independence and outcomes across multiple houses, with person-centred plans and clear team visibility.",
    points: ["Outcomes & future goals", "Multi-house / multi-site working", "Person-centred support"],
  },
  {
    label: "Residential Care",
    to: "/residential-care",
    icon: Building2,
    img: "https://images.unsplash.com/photo-1762955911431-4c44c7c3f408?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    desc: "Connect resident records, care planning, medication and observations with home-level oversight for managers.",
    points: ["Resident records & care planning", "Medication & observations", "Home-level dashboards"],
  },
  {
    label: "Domiciliary Care",
    to: "/domiciliary-care",
    icon: Home,
    img: "https://images.unsplash.com/photo-1658314755707-1fbdf7c40145?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    desc: "From scheduling and rota to care records and attendance — keep community-based services connected and visible.",
    points: ["Visits & scheduling", "Care records & attendance", "Operational visibility"],
  },
];

export default function Solutions() {
  useSEO(
    "Care Solutions — Supported Living, Residential & Domiciliary Care | AYOBELL",
    "AYOBELL supports supported living, residential care and domiciliary care providers with connected care planning, medication, workforce, governance and oversight."
  );
  return (
    <>
      <PageHero
        eyebrow="Solutions"
        title="Built for the way you deliver care."
        subtitle="AYOBELL adapts to your service model — supported living, residential care or domiciliary care — bringing care, people and governance into one connected platform."
        primary={{ to: "/contact", label: "Book a Demo" }}
        secondary={{ to: "/platform", label: "Explore the Platform" }}
        screenKey="dashboard"
        screenComp={ManagerDashboard}
      />

      <section className="bg-white py-20 sm:py-28 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <SectionHead eyebrow="Choose your setting" title="One platform, tailored to your service." desc="Each solution focuses on the workflows that matter most for your care model." />
          <Stagger className="grid md:grid-cols-3 gap-6 mt-14">
            {SECTORS.map((s) => (
              <StaggerItem key={s.label}>
                <div className="group h-full rounded-3xl overflow-hidden border border-slate-200 bg-white hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 flex flex-col">
                  <div className="relative h-48 overflow-hidden">
                    <img src={s.img} alt={`${s.label} care setting`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 to-transparent" />
                    <div className="absolute bottom-4 left-4 flex items-center gap-2 text-white">
                      <span className="w-8 h-8 rounded-lg bg-white/20 backdrop-blur flex items-center justify-center"><s.icon className="w-4 h-4" /></span>
                      <span className="font-display font-bold text-lg">{s.label}</span>
                    </div>
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    <p className="text-sm text-slate-600 leading-relaxed">{s.desc}</p>
                    <ul className="mt-4 space-y-2">
                      {s.points.map((p) => (
                        <li key={p} className="flex items-start gap-2 text-sm text-slate-600"><CheckCircle2 className="w-4 h-4 mt-0.5 text-teal-500 shrink-0" />{p}</li>
                      ))}
                    </ul>
                    <Link to={s.to} data-testid={`solutions-card-${s.label.toLowerCase().replace(/\s+/g, "-")}`} className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-teal-700 hover:gap-2.5 transition-all">
                      Explore {s.label} <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      <CTABand />
    </>
  );
}
